import { ReactNode } from 'react'
import { match } from '@st/pattern'
import { BoxConstraints, RenderBox } from '../rendering'
import { renderElement } from '../document'

export type FloatingPlacement =
  | 'top-start'
  | 'top'
  | 'top-end'
  | 'right-start'
  | 'right'
  | 'right-end'
  | 'bottom-start'
  | 'bottom'
  | 'bottom-end'
  | 'left-start'
  | 'left'
  | 'left-end'

type FloatingProps = {
  placement: FloatingPlacement
  offset: number
  floating: ReactNode
  children: ReactNode
}

class FloatingRenderBox extends RenderBox<Pick<FloatingProps, 'placement' | 'offset'>> {
  performLayout(constraints: BoxConstraints): void {
    if (this.children.length != 2)
      throw new Error('Floating expects a reference and floating child')

    const reference = this.children[0]
    const floating = this.children[1]

    reference.performLayout(constraints)
    reference.box.x = 0
    reference.box.y = 0
    floating.performLayout(BoxConstraints.loosen(constraints))

    const position = floatingPosition(
      this.props.placement,
      this.props.offset,
      reference.box.width,
      reference.box.height,
      floating.box.width,
      floating.box.height
    )
    floating.box.x = position.x
    floating.box.y = position.y
    this.box = { ...reference.box }
  }
}

/**
 * Keeps the reference in normal flow and places `floating` relative to its
 * laid-out bounds. The floating child consumes no flow space, but remains in
 * the same render tree so paint and annotation tracing share one geometry.
 */
export function Floating({ placement, offset, floating, children }: FloatingProps) {
  return renderElement(new FloatingRenderBox({ placement, offset }), children, floating)
}

function floatingPosition(
  placement: FloatingPlacement,
  offset: number,
  referenceWidth: number,
  referenceHeight: number,
  floatingWidth: number,
  floatingHeight: number
) {
  const startX = 0
  const centerX = (referenceWidth - floatingWidth) / 2
  const endX = referenceWidth - floatingWidth
  const startY = 0
  const centerY = (referenceHeight - floatingHeight) / 2
  const endY = referenceHeight - floatingHeight

  return match(placement)
    .with('top-start', () => ({ x: startX, y: -floatingHeight - offset }))
    .with('top', () => ({ x: centerX, y: -floatingHeight - offset }))
    .with('top-end', () => ({ x: endX, y: -floatingHeight - offset }))
    .with('right-start', () => ({ x: referenceWidth + offset, y: startY }))
    .with('right', () => ({ x: referenceWidth + offset, y: centerY }))
    .with('right-end', () => ({ x: referenceWidth + offset, y: endY }))
    .with('bottom-start', () => ({ x: startX, y: referenceHeight + offset }))
    .with('bottom', () => ({ x: centerX, y: referenceHeight + offset }))
    .with('bottom-end', () => ({ x: endX, y: referenceHeight + offset }))
    .with('left-start', () => ({ x: -floatingWidth - offset, y: startY }))
    .with('left', () => ({ x: -floatingWidth - offset, y: centerY }))
    .with('left-end', () => ({ x: -floatingWidth - offset, y: endY }))
    .exhaustive()
}
